import React, { createContext, useContext, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EnhancedDriverData } from '../types/driverData'
import { useDriverData } from '../queries/useDriverData'
import { useCtxDimensions } from '../../dimension/queries/useCtxDimensions'
import { useCtxBudgetDrivers } from '../../budgetDrivers/queries/useCtxBudgetDrivers'
import { enhanceDriverData } from '../utils/dataEnhancement'
import { BudgetDriver } from '../../../types/budgetDriver/BudgetDriver'

interface DriverDataContextValue {
  driverData: EnhancedDriverData[]
  budgetDriverMap: Map<number, BudgetDriver>
  isLoading: boolean
}

const DriverDataContext = createContext<DriverDataContextValue | null>(null)

interface DriverDataProviderProps {
  children: ReactNode
}

/**
 * Provider for driver data - fetches once and shares via context
 * Use this to avoid creating multiple React Query observers
 */
export const DriverDataProvider: React.FC<DriverDataProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const { data: rawDriverData = [], isLoading: isDriverDataLoading } = useDriverData()
  const { data: dimensions = [], isLoading: isDimensionsLoading } = useCtxDimensions()
  const { data: budgetDrivers = [], isLoading: isBudgetDriversLoading } = useCtxBudgetDrivers()

  // Create budget driver lookup map using formula.id as key (matches DriverData.formulaId)
  const budgetDriverMap = useMemo(() => {
    return new Map(budgetDrivers.map(d => [d.formula.id, d]))
  }, [budgetDrivers])

  // Create dimension lookup map
  const dimensionMap = useMemo(() => {
    const map = new Map(dimensions.map(d => [d.dimensionId, d.name]))
    // Add root dimension manually
    map.set('null', t('global:enterpriseLevel'))
    return map
  }, [dimensions, t])

  // Enhance driver data with budget driver and dimension names
  const driverData = useMemo(
    () => enhanceDriverData(rawDriverData, budgetDriverMap, dimensionMap),
    [rawDriverData, budgetDriverMap, dimensionMap]
  )

  const isLoading = isDriverDataLoading || isDimensionsLoading || isBudgetDriversLoading

  return (
    <DriverDataContext.Provider value={{ driverData, budgetDriverMap, isLoading }}>
      {children}
    </DriverDataContext.Provider>
  )
}

/**
 * Hook to access driver data from context
 * Must be used within DriverDataProvider
 */
export const useDriverDataContext = (): DriverDataContextValue => {
  const context = useContext(DriverDataContext)

  if (!context) {
    throw new Error('useDriverDataContext must be used within DriverDataProvider')
  }

  return context
}
